import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Car, Truck, ShoppingCart, TShirt, Sparkle } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the intended redirect URL from state
  const from = location.state?.from;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      
      // Redirect to intended URL if available, otherwise to role-based dashboard
      if (from && from.startsWith('/customer/')) {
        navigate(from);
      } else if (user.role === 'owner') {
        navigate('/owner/dashboard');
      } else if (user.role === 'worker') {
        navigate('/worker/dashboard');
      } else {
        navigate('/customer/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Invalid email or password. Please try again.';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 text-white relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-1/4 w-[400px] h-[400px] bg-[#D4AF37]/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-20 right-1/4 w-[300px] h-[300px] bg-[#F4C2C2]/10 rounded-full blur-[80px]" />
        </div>
        
        <div className="relative z-10 text-center max-w-lg">
          {/* Logo */}
          <div className="mb-8">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] rounded-2xl flex items-center justify-center mb-6 shadow-2xl shadow-[#D4AF37]/20">
              <span className="text-black font-bold text-3xl">H</span>
            </div>
            <h1 className="text-5xl font-bold font-['Cormorant_Garamond'] tracking-tight">
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2] bg-clip-text text-transparent">NC Help Hub</span>
            </h1>
            <p className="text-white/60 text-lg mt-2">Local Rides & Everyday Services</p>
          </div>
          
          {/* Service Icons - 5 Core Services: Rides PRIMARY */}
          <div className="grid grid-cols-5 gap-3 mb-10">
            <div className="text-center group">
              <div className="w-11 h-11 bg-[#8B5CF6]/20 border-2 border-[#8B5CF6]/50 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:border-[#8B5CF6] transition-colors">
                <Car className="w-5 h-5 text-[#8B5CF6]" weight="duotone" />
              </div>
              <p className="text-xs text-[#A78BFA] font-medium">Rides</p>
            </div>
            <div className="text-center group">
              <div className="w-11 h-11 bg-[var(--background-paper)]/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:border-[#F4C2C2]/50 transition-colors">
                <TShirt className="w-5 h-5 text-[#F4C2C2]" weight="duotone" />
              </div>
              <p className="text-xs text-white/50">Laundry</p>
            </div>
            <div className="text-center group">
              <div className="w-11 h-11 bg-[var(--background-paper)]/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:border-[#3B82F6]/50 transition-colors">
                <Truck className="w-5 h-5 text-[#3B82F6]" weight="duotone" />
              </div>
              <p className="text-xs text-white/50">Courier</p>
            </div>
            <div className="text-center group">
              <div className="w-11 h-11 bg-[var(--background-paper)]/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:border-[#EC4899]/50 transition-colors">
                <ShoppingCart className="w-5 h-5 text-[#EC4899]" weight="duotone" />
              </div>
              <p className="text-xs text-white/50">Errands</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex justify-center gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-[#D4AF37] font-['Cormorant_Garamond']">Local</div>
              <div className="text-xs text-white/40">Tarboro Owned</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#F4C2C2] font-['Cormorant_Garamond']">Fast</div>
              <div className="text-xs text-white/40">Same Day</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#D4AF37] font-['Cormorant_Garamond']">24/7</div>
              <div className="text-xs text-white/40">Online Booking</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 bg-[#0A0A0A]">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md pb-24 lg:pb-0"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-14 h-14 mx-auto bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] rounded-xl flex items-center justify-center mb-3">
              <span className="text-black font-bold text-xl">H</span>
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2] bg-clip-text text-transparent font-['Cormorant_Garamond']">
              NC Help Hub
            </h1>
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white font-['Cormorant_Garamond']">Welcome Back</h2>
            <p className="text-white/50 mt-2">Sign in to continue to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="form-group">
              <Label htmlFor="email" className="text-white/80 font-medium">Email Address</Label>
              <div className="relative mt-2">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-12 rounded-xl bg-[#111111] border border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] transition-colors w-full"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="form-group">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-white/80 font-medium">Password</Label>
                <Link to="/forgot-password" className="text-sm text-[#D4AF37] hover:text-[#E6C965] transition-colors">
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-12 rounded-xl bg-[#111111] border border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] pr-12 transition-colors w-full"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-white/20 bg-[#111111] text-[#D4AF37] focus:ring-[#D4AF37]/20"
                />
                <span className="text-sm text-white/60">Remember me</span>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#D4AF37] hover:bg-[#E6C965] text-black font-semibold rounded-xl transition-colors"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Signing in...
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  Sign In
                  <ArrowRight className="w-5 h-5" />
                </div>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-white/50">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-[#D4AF37] hover:text-[#E6C965] font-medium transition-colors" data-testid="signup-link">
                Sign up
              </Link>
            </p>
          </div>
          
          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-white/40 text-sm text-center mb-4">or</p>
            <div className="flex flex-col gap-3">
              <Link to="/apply" className="text-center">
                <Button 
                  variant="outline" 
                  className="w-full border-[#F4C2C2]/30 text-[#F4C2C2] hover:bg-[#F4C2C2]/10 rounded-xl"
                  data-testid="apply-worker-link"
                >
                  Apply as Worker
                </Button>
              </Link>
              <Link to="/careers" className="text-center">
                <Button 
                  variant="ghost" 
                  className="w-full text-white/50 hover:text-white hover:bg-[var(--background-paper)]/5 rounded-xl"
                  data-testid="view-positions-link"
                >
                  View Open Positions
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Trust badges */}
          <div className="mt-8 flex justify-center gap-6 text-white/30 text-xs">
            <span>Trusted</span>
            <span>Fast & Local</span>
            <span>Professional</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
